import React from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const SessionModal = ({
  open,
  onClose,
  selectedRecord,
  lastSession,
  colors,
  username,
  setUsername,
  date,
  setDate,
  sets,
  handleChangeSet,
  handleAddSubSet,
  handleDeleteSet,
  handleCopyToAll,
  handleCopySetDown,
  note,
  setNote,
  quickNotes,
  handleQuickNote,
  handleSubmit,
  handleDelete,
  routineBlock,
  onCopyLastSession,
}) => (
  <Modal open={open} onClose={onClose}>
    <Box
      sx={{
        bgcolor: "background.paper",
        p: { xs: 2, md: 4 },
        m: "auto",
        mt: { xs: 2, md: 10 },
        width: "95%",
        maxWidth: 700,
        borderRadius: 3,
        maxHeight: "95vh",
        overflowY: "auto",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          {selectedRecord?.id ? "Editar Registro" : "Registrar Series"}
        </Typography>
      </Box>

      {!selectedRecord?.id && lastSession && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: colors.primary[900],
            borderRadius: 2,
            border: "1px dashed",
            borderColor: colors.primary[800],
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="caption" display="block" fontWeight="bold">
              Última sesión ({new Date(lastSession.date).toLocaleDateString("es-ES")}):
            </Typography>
            <Button
              size="small"
              variant="outlined"
              sx={{ borderColor: colors.primary[100], color: colors.primary[100] }}
              onClick={onCopyLastSession}
            >
              Copiar
            </Button>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
            {lastSession.setData?.map((s, i) => (
              <Chip
                key={i}
                label={`${s.reps} reps × ${(s.total || 0).toFixed(1)} kg`}
                size="small"
                sx={{ fontSize: "0.7rem" }}
              />
            ))}
          </Box>
          {lastSession.notes && (
            <Typography
              variant="caption"
              sx={{ display: "block", fontStyle: "italic", color: "text.secondary", mt: 1 }}
            >
              Notas: {lastSession.notes}
            </Typography>
          )}
        </Box>
      )}

      {routineBlock && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: "action.hover",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            Plan: {routineBlock.sets} series · {routineBlock.reps} reps
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Capturadas: {sets.length} series
          </Typography>
        </Box>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={selectedRecord?.id ? 6 : 12}>
          {selectedRecord?.id ? (
            <FormControl fullWidth>
              <InputLabel>Usuario</InputLabel>
              <Select value={username} label="Usuario" onChange={(e) => setUsername(e.target.value)}>
                <MenuItem value="Vannesa Santamaria">Vannesa Santamaria</MenuItem>
                <MenuItem value="David Acevedo">David Acevedo</MenuItem>
              </Select>
            </FormControl>
          ) : (
            <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Usuario:</strong> {username}
              </Typography>
            </Box>
          )}
        </Grid>
        {selectedRecord?.id && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Fecha"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        )}
      </Grid>

      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1, fontWeight: "bold" }}>
        Series
      </Typography>

      {sets.map((set, idx) => (
        <Box
          key={idx}
          sx={{
            mb: 2,
            p: 2,
            bgcolor: "action.hover",
            borderRadius: 2,
            border: "2px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Serie #{idx + 1}
            </Typography>
            <Box>
              <IconButton size="small" onClick={() => handleAddSubSet(idx)} color="primary">
                <AddIcon fontSize="small" />
              </IconButton>
              {sets.length > 1 && (
                <IconButton size="small" onClick={() => handleDeleteSet(idx)} color="error">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>

          <Grid container spacing={1}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Reps"
                type="number"
                value={set.reps}
                onChange={(e) => handleChangeSet(idx, "reps", e.target.value)}
                onDoubleClick={() => handleCopyToAll(idx, "reps")}
                size="small"
                InputProps={{ inputProps: { min: 0, style: { fontSize: "1rem" } } }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Libras"
                type="number"
                value={set.weightLb || ""}
                onChange={(e) => handleChangeSet(idx, "weightLb", e.target.value)}
                onDoubleClick={() => handleCopyToAll(idx, "weightLb")}
                size="small"
                InputProps={{ inputProps: { min: 0, step: 5, style: { fontSize: "1rem" } } }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Kilos"
                type="number"
                value={set.weightKg || ""}
                onChange={(e) => handleChangeSet(idx, "weightKg", e.target.value)}
                onDoubleClick={() => handleCopyToAll(idx, "weightKg")}
                size="small"
                InputProps={{ inputProps: { min: 0, step: 2.5, style: { fontSize: "1rem" } } }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              variant="text"
              onClick={() => handleCopySetDown(idx)}
              disabled={idx === sets.length - 1}
            >
              Copiar esta serie hacia abajo
            </Button>
          </Box>

          <Box sx={{ mt: 1, p: 1, bgcolor: "background.paper", borderRadius: 1, textAlign: "center" }}>
            <Typography variant="body2" color="primary" fontWeight="bold">
              Total: {(set.total || 0).toFixed(2)} kg
            </Typography>
          </Box>
        </Box>
      ))}

      <Typography variant="caption" display="block" sx={{ mb: 1, fontStyle: "italic", color: "text.secondary" }}>
        Tip: Doble clic copia hacia abajo o usa "Copiar esta serie hacia abajo" para móvil
      </Typography>

      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
        {quickNotes.map((text) => (
          <Chip
            key={text}
            label={text}
            size="small"
            variant="outlined"
            onClick={() => handleQuickNote(text)}
            sx={{ fontSize: "0.7rem", height: 24, px: 0.75 }}
          />
        ))}
      </Box>

      <TextField
        fullWidth
        label="Notas"
        multiline
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        sx={{ mb: 2, mt: 1 }}
      />

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 4 }}>
        <Button variant="outlined" onClick={onClose} sx={{ flex: 1, minWidth: 120 }}>
          Cancelar
        </Button>

        {selectedRecord?.id ? (
          <>
            <Button variant="outlined" color="error" onClick={handleDelete} sx={{ flex: 1, minWidth: 120 }}>
              Eliminar
            </Button>
            <Button variant="contained" color="primary" onClick={() => handleSubmit(false)} sx={{ flex: 1, minWidth: 140 }}>
              Guardar
            </Button>
          </>
        ) : (
          <Button variant="contained" color="primary" onClick={() => handleSubmit(false)} sx={{ flex: 1, minWidth: 140 }}>
            Guardar
          </Button>
        )}
      </Box>
    </Box>
  </Modal>
);

export default SessionModal;
